import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useParams } from "react-router-dom";
import { fetchCaseById } from "../../store/user/userSlice";
import React from "react";

// ---------- Types ----------
interface InstanceData {
  [key: string]: any;
}

interface ServiceData {
  instanceDetails?: InstanceData[];
  [key: string]: any;
}

interface Services {
  [serviceName: string]: ServiceData;
}

interface ParsedDetails {
  services?: Services;
  [key: string]: any;
}

// ---------- Utilities ----------
const formatLabel = (label: string) =>
  label
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const ARCH_LABELS: Record<string, string> = {
  upper: "Maxilla / Upper",
  lower: "Mandible / Lower",
  both: "Both",
};

const formatDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        item !== null && typeof item === "object"
          ? formatDisplayValue(item)
          : String(item),
      )
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => {
        const pretty =
          k === "arch" && typeof v === "string"
            ? ARCH_LABELS[v] || v
            : formatDisplayValue(v);
        return `${formatLabel(k)}: ${pretty}`;
      })
      .join(" · ");
  }
  return String(value);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const KeyValue: React.FC<{ label: string; value: any }> = React.memo(
  ({ label, value }) => {
    if (value === null || value === undefined || value === "") return null;
    const display = formatDisplayValue(value);
    if (!display) return null;
    return (
      <div className="flex justify-between border-b py-1 text-sm last:border-none">
        <div className="font-medium text-gray-700 w-1/2 pr-2">
          {formatLabel(label)}:
        </div>
        <div className="text-gray-800 w-1/2 pl-2 break-words whitespace-pre-wrap">
          {display}
        </div>
      </div>
    );
  }
);

const InstanceCard: React.FC<{ instance: InstanceData; index: number }> =
  React.memo(({ instance, index }) => (
    <div className="border rounded p-2 bg-gray-50 shadow-sm mb-2">
      <h5 className="text-sm font-semibold text-gray-700 mb-1">
        Instance {index + 1}
      </h5>
      <div className="space-y-1 grid grid-cols-2">
        {Object.entries(instance).map(([k, v]) => (
          <KeyValue key={k} label={k} value={v} />
        ))}
      </div>
    </div>
  ));

// ---------- Component ----------
const CaseDetails = () => {
  // const dispatch = useAppDispatch();
  // const { case_id } = useParams<{ case_id: string }>();

  const { selectedCase, isLoading } = useAppSelector(
    (state) => state.adminCases
  );

  // Fetch once
  // useEffect(() => {
  //   if (case_id) dispatch(fetchCaseById(case_id));
  // }, [dispatch, case_id]);

  // Parse JSON only when details_json changes
  const parsedDetails: ParsedDetails = useMemo(() => {
    if (!selectedCase?.details_json) return {};
    try {
      return selectedCase.details_json as ParsedDetails;
    } catch {
      console.error("Failed to parse details_json");
      return {};
    }
  }, [selectedCase?.details_json]);

  if (isLoading)
    return <p className="text-gray-600">Loading case details...</p>;
  if (!selectedCase) return <p className="text-gray-500">No case found.</p>;

  return (
    <div className="w-full mx-auto p-2 text-sm bg-white">
      {/* Treatment Details */}
      {parsedDetails && Object.keys(parsedDetails).length > 0 ? (
        <div>
          <h3 className="text-md font-semibold text-[#1DA8C6] mb-1 border-b pb-1">
            Treatment Details
          </h3>

          {/* Non-service fields */}
          <div className="space-y-1 mb-2 grid grid-cols-2">
            {Object.entries(parsedDetails)
              .filter(
                ([key, value]) =>
                  key !== "services" &&
                  key !== "patientNames" &&
                  value !== "" &&
                  value !== null &&
                  value !== undefined
              )
              .map(([key, value]) => {
                if (isPlainObject(value)) {
                  return (
                    <div key={key} className="col-span-2 mb-2">
                      <h4 className="mb-1 border-b pb-1 text-md font-semibold capitalize text-[#2fa9bf]">
                        {formatLabel(key)}
                      </h4>
                      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                        {Object.entries(value)
                          .filter(
                            ([, nested]) =>
                              nested !== "" &&
                              nested !== null &&
                              nested !== undefined,
                          )
                          .map(([nestedKey, nestedValue]) => (
                            <KeyValue
                              key={nestedKey}
                              label={nestedKey}
                              value={
                                nestedKey === "arch" &&
                                typeof nestedValue === "string"
                                  ? ARCH_LABELS[nestedValue] || nestedValue
                                  : nestedValue
                              }
                            />
                          ))}
                      </div>
                    </div>
                  );
                }
                return <KeyValue key={key} label={key} value={value} />;
              })}
          </div>

          {/* Services */}
          {parsedDetails.services &&
            Object.entries(parsedDetails.services).map(([section, data]) => {
              const service = data as ServiceData;
              return (
                <div key={section} className="mb-2">
                  <h4 className="text-md font-semibold text-[#2fa9bf] mb-1 border-b pb-1 capitalize">
                    {formatLabel(section)}
                  </h4>
                  {service.instanceDetails?.map((inst, idx) => (
                    <InstanceCard key={idx} instance={inst} index={idx} />
                  ))}
                  <div className="space-y-1">
                    {Object.entries(service)
                      .filter(([k]) => k !== "instanceDetails")
                      .map(([k, v]) => (
                        <KeyValue key={k} label={k} value={v} />
                      ))}
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        <p className="text-gray-500 italic">No treatment details available.</p>
      )}
    </div>
  );
};

export default CaseDetails;
